import { OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import {
  Component,
  ElementRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { DragData } from '@modules/drag-n-drop/types/drag-data';
import { NestedDataItem } from '@modules/drag-n-drop/types/nested-item';
import { TaskQuickFormComponent } from '@modules/elements/components/quick-forms/task-quick-form/task-quick-form.component';
import { ModalService } from '@modules/modal/services/modal.service';
import { BoardService } from '@modules/tasks/services/board.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { Column, type ColumnFormGroup } from '@modules/tasks/types/column';
import { Section } from '@modules/common/types/section';
import { SectionTaskAppearance } from '@modules/tasks/types/section-task-appearance';
import { Task } from '@modules/tasks/types/task';
import { newTaskDropPredicate, seedFromDroppedEntity } from '@modules/tasks/utils/new-task-drop.util';
import { isBoardTabTaskDrop, PROJECT_BOARD_TAB } from '@modules/tasks/utils/project-form-dnd.util';
import { Subject, merge } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-project-board-column',
  templateUrl: './project-board-column.component.html',
  styleUrls: ['./project-board-column.component.less'],
  standalone: false,
})
export class ProjectBoardColumnComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly SECTION_LABEL_OFFSET = 2;

  @Input() column: Column;
  @Input() section: Section;
  @Input() appearance: SectionTaskAppearance = 'board-large';

  private readonly titleInputRef = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  public dndPredicate = (dragData: DragData): boolean => isBoardTabTaskDrop(PROJECT_BOARD_TAB, dragData, {
    id: this.column?.projectId,
    accountId: this.boardService.itemRequestConfig().requestContext?.get(OWNING_SPACE_ID),
  });
  public readonly newTaskDndPredicate = newTaskDropPredicate;
  public tasks: Task[] = [];
  public columnForm: ColumnFormGroup;
  public collapsed = false;
  public tasksChanges = new Subject<NestedDataItem<Task>[]>();
  public tasksCount = 0;
  public quickTask: Task;

  private alive = new Subject<void>();
  private columnChanged = new Subject<void>();
  private sectionChanged = new Subject<void>();

  /**
   * A task can only be added once the column, and its section, are named. Reads the live form value,
   * so a freshly typed title enables it immediately — the List column applies the same rule to the
   * persisted `column().title` instead, and so enables it only after the rename is saved.
   */
  protected get canAddTask(): boolean {
    return !!this.columnForm.controls.title.value && (!this.section || !!this.section.title);
  }

  protected get sectionPosition(): number | undefined {
    if (!this.section) {
      return undefined;
    }
    return this.section.position + ProjectBoardColumnComponent.SECTION_LABEL_OFFSET;
  }

  private createInProgress = false;

  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);

  constructor(
    private boardService: BoardService,
    private tasksService: TasksService,
  ) {}

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.columnForm = this.column.asFormGroup();

    this.columnChanged
      .pipe(
        switchMap(() => this.boardService.getColumnCollapsed(this.column.id)),
        takeUntil(this.alive),
      )
      .subscribe((value: boolean) => (this.collapsed = value));

    merge(this.columnChanged, this.sectionChanged)
      .pipe(
        switchMap(() => this.boardService.getTasks(this.column, this.section)),
        takeUntil(this.alive),
      )
      .subscribe((tasks) => {
        this.tasks = tasks;
      });

    this.columnChanged
      .pipe(
        switchMap(() => this.boardService.getTasksCount(this.column)),
        takeUntil(this.alive),
      )
      .subscribe((count) => {
        this.tasksCount = count;
      });

    this.columnChanged.next();
    this.sectionChanged.next();

    this.tasksChanges.pipe(takeUntil(this.alive)).subscribe((changes) => {
      this.boardService.updateTasks(changes, this.section?.id, this.column.id);
    });

    this.boardService.columnAddFocus$.pipe(takeUntil(this.alive)).subscribe((originatingSectionId) => {
      if (this.column?.id !== 'temp') return;
      if ((this.section?.id ?? null) !== originatingSectionId) return;
      this.focusTitle();
    });

    // A column added by "Add new column" is created after the event above: it takes the cursor here.
    if (this.column?.id === 'temp' && this.boardService.claimColumnFocus(this.section?.id ?? null)) {
      this.focusTitle();
    }
  }

  private focusTitle(): void {
    setTimeout(() => this.titleInputRef()?.nativeElement?.focus(), 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('column' in changes) {
      this.columnChanged.next();
    }

    if ('section' in changes) {
      this.sectionChanged.next();
    }
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleBlur() {
    if (this.columnForm.get('title').dirty && this.columnForm.get('title').value.trim() !== '') {
      this.boardService.updateColumn(Column.fromFormGroup(this.columnForm));
    }
  }

  handleNewColumnRight() {
    if (this.column.temp) {
      return;
    }
    this.boardService.addColumn(true, this.column.position);
  }

  collapse() {
    this.boardService.setColumnCollapsed(this.column.id, !this.collapsed);
  }

  handleNewTask(seed: Partial<Task> = {}): void {
    this.quickTask = new Task({
      ...seed,
      accountId: this.boardService.itemRequestConfig().requestContext?.get(OWNING_SPACE_ID) ?? undefined,
      projectId: this.column.projectId,
      sectionId: this.section?.id,
      columnId: this.column.id,
    });
  }

  /** Dropping an entity on "Add a new task" opens the quick form pre-filled from it (SBUX-897). */
  protected handleNewTaskDrop(dragData: DragData): void {
    this.handleNewTask(seedFromDroppedEntity(dragData));
  }

  handleQuickFormClose() {
    this.quickTask = null;
  }

  handleQuickFormSave(task: Task, quickForm?: TaskQuickFormComponent) {
    if (this.createInProgress) return;
    this.createInProgress = true;
    task.columnId = this.column.id;
    task.sectionId = this.section?.id;
    task.position =
      this.tasks.reduce(
        (memo, { parentId, position }) => (parentId === null && memo < position ? position : memo),
        -1,
      ) + 1;
    task.boardPosition =
      this.tasks.reduce(
        (memo, t) => (t.parentId === null && memo < (t.boardPosition ?? 0) ? (t.boardPosition ?? 0) : memo),
        -1,
      ) + 1;

    this.tasksService
      .create(task, this.boardService.itemRequestConfig())
      .pipe(
        takeUntil(this.alive),
        finalize(() => {
          this.createInProgress = false;
        }),
      )
      .subscribe({
        next: (savedTask) => {
          this.boardService.addTask(savedTask);
          this.quickTask = null;
        },
        error: (error) => quickForm?.handleError(error),
      });
  }

  handleQuickFormMore(task: Task) {
    this.modalService.openFullForm(task, this.injector, this.boardService.itemRequestConfig().requestContext);
  }

  /**
   * Helpers
   */

  tasksTrackByFn(task: Task): string {
    return task?.id;
  }

  public close() {
    // TODO
  }
}
